/**
 * Shared hook for AI generation dialog flow.
 * Encapsulates: step state machine, abort controller, elapsed timer,
 * prompt editing lifecycle, progress/media tracking, and review item handlers.
 * Used by AiEnhanceDialog, AiVideoDialog, and AiTtsDialog.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAiStore } from '@/store/aiStore.ts';

// ─── Types ───────────────────────────────────────────────────────────────

export type AiDialogStep = 'options' | 'generating' | 'review' | 'done';

/** Minimum shape for review items — each dialog extends with domain fields */
export interface BaseReviewItem {
  id: string;
  success: boolean;
  accepted: boolean;
}

export interface GenerationProgress {
  current: number;
  total: number;
  message: string;
}

interface Options {
  open: boolean;
  /** The computed default prompt (changes when metadata/scope changes) */
  defaultPrompt: string;
}

// ─── Hook ────────────────────────────────────────────────────────────────

export function useAiDialogFlow<R extends BaseReviewItem>({ open, defaultPrompt }: Options) {
  const aiUseMetadata = useAiStore(s => s.aiUseMetadata);

  const [step, setStep] = useState<AiDialogStep>('options');
  const [editablePrompt, setEditablePrompt] = useState('');
  const [promptInitialized, setPromptInitialized] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [completedMedia, setCompletedMedia] = useState<Map<string, string>>(new Map());
  const [reviewItems, setReviewItems] = useState<R[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  // Load catalog when dialog opens
  useEffect(() => {
    if (open) useAiStore.getState().loadModelCatalog();
  }, [open]);

  // Elapsed timer — runs only during generation
  useEffect(() => {
    if (step !== 'generating') { setElapsedSeconds(0); return; }
    const iv = setInterval(() => setElapsedSeconds(prev => prev + 1), 1000);
    return () => clearInterval(iv);
  }, [step]);

  // Initialize prompt on first open
  useEffect(() => {
    if (open && !promptInitialized) {
      setEditablePrompt(defaultPrompt);
      setPromptInitialized(true);
    }
  }, [open, defaultPrompt, promptInitialized]);

  // Resync prompt when "Use Metadata" toggles while dialog is open
  const prevMetadata = useRef(aiUseMetadata);
  useEffect(() => {
    if (open && promptInitialized && aiUseMetadata !== prevMetadata.current) {
      setEditablePrompt(defaultPrompt);
    }
    prevMetadata.current = aiUseMetadata;
  }, [open, promptInitialized, aiUseMetadata, defaultPrompt]);

  // Reset prompt state when dialog closes
  useEffect(() => {
    if (!open) {
      setPromptInitialized(false);
      setPromptOpen(false);
    }
  }, [open]);

  // Cleanup abort on unmount
  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────

  /** Transition to generating step. Returns the AbortController for the caller. */
  const startGeneration = useCallback((): AbortController => {
    setStep('generating');
    setCompletedMedia(new Map());
    setProgress(null);
    const abort = new AbortController();
    abortRef.current = abort;
    return abort;
  }, []);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    setStep('options');
  }, []);

  // ── Review handlers ──────────────────────────────────────────────────

  const handleAcceptAll = useCallback(() => {
    setReviewItems(prev => prev.map(item => ({ ...item, accepted: item.success })));
  }, []);

  const handleRejectAll = useCallback(() => {
    setReviewItems(prev => prev.map(item => ({ ...item, accepted: false })));
  }, []);

  const handleToggleItem = useCallback((id: string) => {
    setReviewItems(prev => prev.map(item =>
      item.id === id ? { ...item, accepted: !item.accepted } : item
    ));
  }, []);

  const acceptedCount = useMemo(
    () => reviewItems.filter(i => i.accepted && i.success).length,
    [reviewItems]
  );

  return {
    // Step machine
    step, setStep,
    // Prompt editing
    editablePrompt, setEditablePrompt,
    promptOpen, togglePromptOpen: useCallback(() => setPromptOpen(p => !p), []),
    // Generation state
    elapsedSeconds,
    progress, setProgress,
    completedMedia, setCompletedMedia,
    // Review
    reviewItems, setReviewItems,
    acceptedCount,
    // Refs & actions
    abortRef,
    startGeneration,
    handleCancel,
    handleAcceptAll,
    handleRejectAll,
    handleToggleItem,
  };
}
